import { useState, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import SearchIcon from '@mui/icons-material/Search'
import { useCorpusData } from '../context/CorpusDataContext'
import CaseStudyDetail from '../components/CaseStudyDetail'

function CaseStudies() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  // Get case studies from corpus data context (fetched from GitHub)
  const { caseStudies, loading, error } = useCorpusData()

  const [search, setSearch] = useState('')
  const [equipmentFilter, setEquipmentFilter] = useState('')
  const [industryFilter, setIndustryFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [selectedStudy, setSelectedStudy] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const handleRowClick = (params) => {
    setSelectedStudy(params.row)
    setDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
  }


  // Get unique values for filters (handle loading state)
  const equipmentTypes = useMemo(() =>
    caseStudies ? [...new Set(caseStudies.map(c => c.equipment_type).filter(Boolean))] : [],
    [caseStudies]
  )
  const industries = useMemo(() =>
    caseStudies ? [...new Set(caseStudies.map(c => c.industry).filter(Boolean))] : [],
    [caseStudies]
  )
  const states = useMemo(() =>
    caseStudies ? [...new Set(caseStudies.map(c => c.state).filter(Boolean))].sort() : [],
    [caseStudies]
  )

  // Filter data
  const filteredData = useMemo(() => {
    if (!caseStudies) return []

    return caseStudies.filter(study => {
      const matchesSearch = search === '' ||
        study.source?.toLowerCase().includes(search.toLowerCase()) ||
        study.industry?.toLowerCase().includes(search.toLowerCase()) ||
        study.pe_name?.toLowerCase().includes(search.toLowerCase())

      const matchesEquipment = equipmentFilter === '' ||
        study.equipment_type === equipmentFilter

      const matchesIndustry = industryFilter === '' ||
        study.industry === industryFilter

      const matchesState = stateFilter === '' ||
        study.state === stateFilter

      return matchesSearch && matchesEquipment && matchesIndustry && matchesState
    })
  }, [caseStudies, search, equipmentFilter, industryFilter, stateFilter])

  // Responsive column definitions
  const columns = useMemo(() => {
    const baseColumns = [
      { field: 'id', headerName: 'ID', width: 80, hideable: false },
      {
        field: 'source',
        headerName: 'Source',
        width: 220,
        flex: isMobile ? 0 : 1.2,
        minWidth: 160,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} arrow>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
              {params.value || '-'}
            </Box>
          </Tooltip>
        )
      },
      {
        field: 'equipment_type',
        headerName: 'Equipment',
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.value || 'N/A'}
            size="small"
            color={params.value ? 'primary' : 'default'}
            variant="outlined"
          />
        )
      },
      {
        field: 'industry',
        headerName: 'Industry',
        width: 150,
        renderCell: (params) => (
          <Tooltip title={params.value?.replace(/_/g, ' ') || ''} arrow>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
              {params.value?.replace(/_/g, ' ') || '-'}
            </Box>
          </Tooltip>
        )
      },
      { field: 'state', headerName: 'State', width: 70 },
      {
        field: 'efficiency_pct',
        headerName: 'Efficiency Improvement (%)',
        width: 150,
        type: 'number',
        renderCell: (params) => (
          <Typography
            color={params.value >= 20 ? 'success.main' : 'text.primary'}
            fontWeight={600}
          >
            {params.value ? `${params.value.toFixed(1)}%` : '-'}
          </Typography>
        )
      },
      {
        field: 'annual_savings',
        headerName: 'Annual Savings',
        width: 130,
        type: 'number',
        renderCell: (params) =>
          params.value ? `$${params.value.toLocaleString()}` : '-'
      },
      {
        field: 'payback_months',
        headerName: 'Payback (mo)',
        width: 110,
        type: 'number',
        renderCell: (params) =>
          params.value ? `${params.value.toFixed(1)}` : '-'
      },
      {
        field: 'pe_name',
        headerName: 'PE Name',
        width: 180,
        flex: isMobile ? 0 : 0.8,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} arrow>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
              {params.value || '-'}
            </Box>
          </Tooltip>
        )
      },
      {
        field: 'test_date',
        headerName: 'Date',
        width: 120,
        renderCell: (params) => {
          if (!params.value) return '-'
          const date = new Date(params.value)
          if (isNaN(date.getTime())) return params.value
          return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
        }
      }
    ]

    // Hide less important columns on mobile/tablet
    if (isMobile) {
      return baseColumns.filter(col =>
        ['id', 'equipment_type', 'efficiency_pct'].includes(col.field)
      )
    }
    if (isTablet) {
      return baseColumns.filter(col =>
        !['pe_name', 'test_date', 'payback_months'].includes(col.field)
      )
    }
    return baseColumns
  }, [isMobile, isTablet])

  const clearFilters = () => {
    setSearch('')
    setEquipmentFilter('')
    setIndustryFilter('')
    setStateFilter('')
  }

  const activeFilters = [equipmentFilter, industryFilter, stateFilter, search].filter(Boolean).length

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Show error state
  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
          Case Studies Explorer
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load case studies: {error}
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Case Studies Explorer
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search case studies..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Equipment</InputLabel>
                <Select
                  value={equipmentFilter}
                  label="Equipment"
                  onChange={(e) => setEquipmentFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {equipmentTypes.map(eq => (
                    <MenuItem key={eq} value={eq}>{eq}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Industry</InputLabel>
                <Select
                  value={industryFilter}
                  label="Industry"
                  onChange={(e) => setIndustryFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {industries.map(ind => (
                    <MenuItem key={ind} value={ind}>
                      {ind.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>State</InputLabel>
                <Select
                  value={stateFilter}
                  label="State"
                  onChange={(e) => setStateFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {states.map(st => (
                    <MenuItem key={st} value={st}>{st}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredData.length} of {caseStudies.length} studies
                </Typography>
                {activeFilters > 0 && (
                  <Chip
                    label={`Clear ${activeFilters} filter${activeFilters > 1 ? 's' : ''}`}
                    size="small"
                    onDelete={clearFilters}
                    color="primary"
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        Click any row to view full case study details
      </Typography>
      <Card>
        <Box sx={{
          height: { xs: 'calc(100vh - 320px)', sm: 'calc(100vh - 280px)', md: 600 },
          minHeight: 400,
          width: '100%'
        }}>
          <DataGrid
            rows={filteredData}
            columns={columns}
            pageSize={isMobile ? 10 : 25}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            onRowClick={handleRowClick}
            density={isMobile ? 'compact' : 'standard'}
            aria-label="Case studies data table"
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': {
                outline: 'none'
              },
              '& .MuiDataGrid-cell:focus-within': {
                outline: `2px solid ${theme.palette.primary.main}`
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.background.default
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: 1.2,
                textAlign: 'center'
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  borderLeft: `3px solid ${theme.palette.primary.main}`
                }
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: `1px solid ${theme.palette.divider}`
              }
            }}
          />
        </Box>
      </Card>

      {/* Case Study Detail Drawer */}
      <CaseStudyDetail
        caseStudy={selectedStudy}
        open={drawerOpen}
        onClose={handleCloseDrawer}
      />
    </Box>
  )
}

export default CaseStudies
